import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Recycle, Plus, Trash2, Calculator as CalcIcon, Download, Mail, TrendingDown, Building2, Calendar, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { organizationsAPI, calculationsAPI } from '../api/client';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

const EnhancedCalculator = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [organizations, setOrganizations] = useState([]);
  const [selectedYear, setSelectedYear] = useState(String(CURRENT_YEAR));
  const [wasteInputs, setWasteInputs] = useState([{
    id: 1,
    waste_stream: 'packaging',
    packaging_type: 'plastic',
    weight_kg: '',
    organization_id: 'none'
  }]);
  const [result, setResult] = useState(null);
  const [multiyearResult, setMultiyearResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calcMode, setCalcMode] = useState('single'); // 'single' | 'multiyear'

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const addWasteInput = () => {
    setWasteInputs([...wasteInputs, {
      id: Date.now(),
      waste_stream: 'packaging',
      packaging_type: 'plastic',
      weight_kg: '',
      organization_id: 'none'
    }]);
  };

  const removeWasteInput = (id) => {
    if (wasteInputs.length > 1) {
      setWasteInputs(wasteInputs.filter(input => input.id !== id));
    }
  };

  const updateWasteInput = (id, field, value) => {
    setWasteInputs(wasteInputs.map(input =>
      input.id === id ? { ...input, [field]: value } : input
    ));
  };

  const buildPayload = () => ({
    waste_inputs: wasteInputs.map(input => ({
      waste_stream: input.waste_stream,
      packaging_type: input.packaging_type || null,
      weight_kg: parseFloat(input.weight_kg),
      organization_id: (input.organization_id && input.organization_id !== 'none') ? input.organization_id : null
    })),
    year: parseInt(selectedYear)
  });

  const validateInputs = () => wasteInputs.every(input =>
    input.waste_stream && input.weight_kg &&
    (input.waste_stream !== 'packaging' || input.packaging_type)
  );

  const handleCalculate = async () => {
    if (!validateInputs()) {
      toast({ title: "Klaida", description: "Užpildykite visus privalomus laukus", variant: "destructive" });
      return;
    }
    setCalculating(true);
    try {
      const response = await calculationsAPI.calculate(buildPayload());
      setResult(response.data);
      setMultiyearResult(null);
      toast({ title: "Sėkmingai apskaičiuota!", description: "Rezultatai rodomi žemiau" });
    } catch (error) {
      toast({ title: "Klaida", description: error.response?.data?.detail || "Nepavyko apskaičiuoti", variant: "destructive" });
    } finally {
      setCalculating(false);
    }
  };

  const handleCalculateMultiyear = async () => {
    if (!validateInputs()) {
      toast({ title: "Klaida", description: "Užpildykite visus privalomus laukus", variant: "destructive" });
      return;
    }
    setCalculating(true);
    try {
      const response = await calculationsAPI.calculateMultiyear(buildPayload());
      setMultiyearResult(response.data.years);
      setResult(null);
      toast({ title: "Prognozė apskaičiuota!", description: "5 metų projekcija rodoma žemiau" });
    } catch (error) {
      toast({ title: "Klaida", description: error.response?.data?.detail || "Nepavyko apskaičiuoti", variant: "destructive" });
    } finally {
      setCalculating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!result?.id) return;
    setLoading(true);
    try {
      const response = await calculationsAPI.downloadPDF(result.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tarsos_mokestis_${result.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: "PDF atsisiųstas!", description: "Patikrinkite savo atsisiuntimus" });
    } catch (error) {
      toast({ title: "Klaida", description: "Nepavyko atsisiųsti PDF", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!result?.id) return;
    setLoading(true);
    try {
      const response = await calculationsAPI.generatePDF(result.id);
      if (response.data.email_configured) {
        toast({ title: "El. laiškas išsiųstas!", description: "Patikrinkite savo el. paštą" });
      } else {
        toast({ title: "PDF sugeneruotas", description: "El. pašto paslauga dar nesukonfigūruota. Naudokite atsisiuntimo funkciją." });
      }
    } catch (error) {
      toast({ title: "Klaida", description: "Nepavyko išsiųsti el. laiško", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getWasteStreamLabel = (stream) => ({
    packaging: 'Pakuotės', weee: 'WEEE (elektros įranga)', oil: 'Alyvos', textile: 'Tekstilė'
  }[stream] || stream);

  const getPackagingTypeLabel = (type) => ({
    plastic: 'Plastikas', glass: 'Stiklas', paper: 'Popierius/Kartonas', metal: 'Metalas', composite: 'Kombinuotos'
  }[type] || type);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-green-600" />
            <span className="text-xl font-semibold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <span className="text-sm text-gray-600">{user?.email}</span>
            <Link to="/dashboard"><Button variant="outline">Atgal</Button></Link>
            <Button variant="outline" onClick={logout}>Atsijungti</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calculator Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <CalcIcon className="w-6 h-6 text-green-600" />
                  Išplėstinis skaičiuotuvas
                </CardTitle>
                <CardDescription>
                  Pridėkite atliekų srautus ir palyginkite organizacijas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Year selector + mode */}
                <div className="flex flex-wrap items-end gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="space-y-1 flex-1 min-w-[140px]">
                    <Label className="flex items-center gap-1 text-blue-900">
                      <Calendar className="w-4 h-4" /> Skaičiavimo metai
                    </Label>
                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YEAR_OPTIONS.map(y => (
                          <SelectItem key={y} value={String(y)}>{y} m.</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 flex-1 min-w-[200px]">
                    <Label className="flex items-center gap-1 text-blue-900">
                      <BarChart3 className="w-4 h-4" /> Skaičiavimo režimas
                    </Label>
                    <Select value={calcMode} onValueChange={setCalcMode}>
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Vienerių metų skaičiavimas</SelectItem>
                        <SelectItem value="multiyear">5 metų prognozė</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Waste inputs */}
                {wasteInputs.map((input, index) => (
                  <Card key={input.id} className="p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-lg">Atliekų srautas #{index + 1}</h3>
                      {wasteInputs.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeWasteInput(input.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Atliekų tipas *</Label>
                        <Select value={input.waste_stream} onValueChange={(v) => updateWasteInput(input.id, 'waste_stream', v)}>
                          <SelectTrigger><SelectValue placeholder="Pasirinkite tipą" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="packaging">Pakuotės</SelectItem>
                            <SelectItem value="weee">WEEE (elektros įranga)</SelectItem>
                            <SelectItem value="oil">Alyvos</SelectItem>
                            <SelectItem value="textile">Tekstilė</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {input.waste_stream === 'packaging' && (
                        <div className="space-y-2">
                          <Label>Pakuotės tipas *</Label>
                          <Select value={input.packaging_type} onValueChange={(v) => updateWasteInput(input.id, 'packaging_type', v)}>
                            <SelectTrigger><SelectValue placeholder="Pasirinkite pakuotę" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="plastic">Plastikas</SelectItem>
                              <SelectItem value="glass">Stiklas</SelectItem>
                              <SelectItem value="paper">Popierius/Kartonas</SelectItem>
                              <SelectItem value="metal">Metalas</SelectItem>
                              <SelectItem value="composite">Kombinuotos</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Svoris (kg per metus) *</Label>
                        <Input type="number" placeholder="1000" value={input.weight_kg}
                          onChange={(e) => updateWasteInput(input.id, 'weight_kg', e.target.value)} min="0" step="0.01" />
                      </div>
                      <div className="space-y-2">
                        <Label>Organizacija (palyginimui)</Label>
                        <Select value={input.organization_id || "none"} onValueChange={(v) => updateWasteInput(input.id, 'organization_id', v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Pasirinkite organizaciją" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Be organizacijos</SelectItem>
                            {organizations.map(org => (
                              <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                ))}

                <Button variant="outline" onClick={addWasteInput} className="w-full border-dashed">
                  <Plus className="w-4 h-4 mr-2" />Pridėti atliekų srautą
                </Button>

                <Button
                  onClick={calcMode === 'multiyear' ? handleCalculateMultiyear : handleCalculate}
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={calculating}
                  size="lg"
                >
                  {calculating ? 'Skaičiuojama...' : calcMode === 'multiyear' ? 'Apskaičiuoti 5 metų prognozę' : `Apskaičiuoti mokestį (${selectedYear} m.)`}
                </Button>
              </CardContent>
            </Card>

            {/* Single year result */}
            {result && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-xl text-green-900">
                    Skaičiavimo rezultatai – {selectedYear} m.
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-white p-6 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-lg mb-4">Santrauka</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="text-gray-600">Bazinis mokestis:</span>
                        <span className="font-semibold text-lg">€{result.total_base_tax.toFixed(2)}</span>
                      </div>
                      {result.total_discount > 0 && (
                        <div className="flex justify-between items-center pb-2 border-b">
                          <span className="text-gray-600">Nuolaida:</span>
                          <span className="font-semibold text-lg text-green-600">-€{result.total_discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2">
                        <span className="text-green-900 font-semibold text-lg">Galutinis mokestis:</span>
                        <span className="font-bold text-2xl text-green-600">€{result.total_final_tax.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {result.potential_savings && Object.keys(result.potential_savings).length > 0 && (
                    <div className="bg-white p-6 rounded-lg border border-green-200">
                      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-green-600" />
                        Organizacijų palyginimas
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(result.potential_savings)
                          .sort((a, b) => b[1] - a[1])
                          .map(([orgId, savings]) => {
                            const org = organizations.find(o => o.id === orgId);
                            if (!org) return null;
                            return (
                              <div key={orgId} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium">{org.name}</span>
                                </div>
                                <span className="text-green-600 font-semibold">Sutaupymas: €{savings.toFixed(2)}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button onClick={handleDownloadPDF} disabled={loading} className="flex-1">
                      <Download className="w-4 h-4 mr-2" />Atsisiųsti PDF
                    </Button>
                    <Button onClick={handleSendEmail} disabled={loading} variant="outline" className="flex-1">
                      <Mail className="w-4 h-4 mr-2" />Siųsti el. paštu
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Multi-year result */}
            {multiyearResult && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-xl text-blue-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    5 metų mokesčių prognozė
                  </CardTitle>
                  <CardDescription className="text-blue-700">
                    Prognozė pagal šiuo metu galiojančius tarifus
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 px-3 text-blue-900">Metai</th>
                          <th className="text-right py-2 px-3 text-blue-900">Bazinis mokestis</th>
                          <th className="text-right py-2 px-3 text-blue-900">Su geriausia org.</th>
                          <th className="text-right py-2 px-3 text-green-700">Sutaupymas</th>
                          <th className="text-left py-2 px-3 text-blue-900">Geriausia org.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(multiyearResult).map((row) => (
                          <tr key={row.year} className={`border-b border-blue-100 ${row.year === parseInt(selectedYear) ? 'bg-blue-100 font-semibold' : 'bg-white'}`}>
                            <td className="py-3 px-3">
                              {row.year} m.
                              {row.year === parseInt(selectedYear) && <span className="ml-2 text-xs text-blue-600">(pasirinkti)</span>}
                            </td>
                            <td className="text-right py-3 px-3">€{row.base_tax.toFixed(2)}</td>
                            <td className="text-right py-3 px-3 text-green-700">€{row.best_org_tax.toFixed(2)}</td>
                            <td className="text-right py-3 px-3 text-green-600 font-medium">€{row.potential_saving.toFixed(2)}</td>
                            <td className="py-3 px-3 text-gray-600 text-xs">{row.best_org_name || '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-blue-600 mt-3">
                    * Jei administratorius pridėjo konkrečių metų tarifus, prognozė juos atspindės. Kitu atveju naudojami dabartiniai tarifai.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Info Sidebar */}
          <div className="space-y-6">
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg text-blue-900">Atliekų srovės</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div><h4 className="font-semibold text-blue-900">Pakuotės</h4><p className="text-blue-800">Plastikas, stiklas, popierius, metalas, kombinuotos pakuotės</p></div>
                <div><h4 className="font-semibold text-blue-900">WEEE</h4><p className="text-blue-800">Elektros ir elektroninės įrangos atliekos</p></div>
                <div><h4 className="font-semibold text-blue-900">Alyvos</h4><p className="text-blue-800">Panaudotos alyvos ir tepalai</p></div>
                <div><h4 className="font-semibold text-blue-900">Tekstilė</h4><p className="text-blue-800">Tekstilės atliekos (nuo 2025 m.)</p></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Organizacijos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {organizations.slice(0, 5).map(org => (
                    <div key={org.id} className="pb-2 border-b last:border-0">
                      <p className="font-medium">{org.name}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCalculator;
